import { Lock } from '../../kit/Lock';
import {MemoryCache} from "../storage/MemoryCache";
import {DateUtils} from "../utils/dateUtils";
import {Logger} from "../logger/logger";
export type QueueEntryState = "Pending" | "PendingParent" | "Error" | "UnrecoverableError";
export interface QueueEntry {
  createdAt?: string;
  state?: QueueEntryState;
  retry?: number;
  waitUntil?: string;
  errorMessage?: string;
}
export class QueueOperationException {
  constructor(public state: QueueEntryState, public message: string) {
  }
}


interface Delivery<T> {
  value: T;
  complete(): Promise<void>;
  failed(errorState: QueueEntryState, errorMessage: string): Promise<void>;
  handleException(logger: Logger, exception: any, entry: Delivery<T>): Promise<void>;
}

export class PersistentQueue<T extends QueueEntry> {
  static async open<T extends QueueEntry>(storage: MemoryCache<T[]>): Promise<PersistentQueue<T>> {
    const queue = (await storage.get('queue')) ?? [];
    return new PersistentQueue<T>(storage, queue);
  }

  private readonly lock = new Lock();

  constructor(public storage: MemoryCache<T[]>, private queue: T[]) {}
  get queueLength() {
    return this.queue.length;
  }

  async length() {
    return await this.lock.run(async () => {
      return this.queue.length;
    });
  }

  async unshift(item: T) {
    if (!item.state) {
      item.state = "Pending";
      item.createdAt = DateUtils.fromDate(new Date());
      item.waitUntil = item.createdAt;
      item.retry = 0;
    }
    await this.lock.run(async () => {
      this.queue = [item, ...this.queue];
      await this.storage.set('queue', this.queue); // this.queue is immutable for some reason
    });
  }

  private isPending(item: T) : boolean {
    const now = DateUtils.fromDate(new Date());
    return (!item.waitUntil || item.waitUntil <= now)
  }
  hasPendingMessages(): boolean {
    return this.queue.some(item => this.isPending(item));
  }

  // The way this works, is that the internal representation of the queue (this.queue) is
  // updated in the pop method.  The resulting changes to the queue ARE NOT written back to
  // indexed db until the completion is called - that way if there is an uncaught exception, the change
  // will be rolled back.  So the first thing the error handler needs to do is also commit it to indicate that
  // the message has been processed.
  async pop(): Promise<Delivery<T> | undefined> {
    const unlock = await this.lock.acquire();
    let item: T|null = null;
    // The queue is in reverse order.  Start with the end and work back until we find one that is pending.
    for (let i = this.queue.length - 1; i >= 0; i--) {
      const currentItem = this.queue[i];
      if (this.isPending(currentItem)) {
        this.queue = [...this.queue.slice(0, i), ...this.queue.slice(i + 1)];
        item = currentItem;
        break;
      }
    }
    if (!item) {
      unlock();
      return undefined;
    }
    return {
      value: item,
      complete: async () => {
        await this.storage.set('queue', this.queue);
        unlock();

      },
      failed: async (state: QueueEntryState, errorMessage: string) => {
        await this.storage.set('queue', this.queue);
        unlock();

        if (state === "UnrecoverableError" || !item) {
          // Do not requeue the message.
          return;
        }

        // Currently the default behaviour is to pop this request back onto the queue to be retried.
        // The obvious problem here is that it will loop forever trying again. Long term, we need a
        // Dead Letter Queue or similar. For now, only retry it on the next iteration batch
        item.state = state;
        item.errorMessage = errorMessage;
        item.retry ||= 0;
        item.retry++;
        if (item.retry && item.retry > 2100) {
          // Throw it away.  We've been trying for over a week - it's never going to work.
          return;
        }
        // Retry straight away the first time, then increase by 1 minute every time until you get to 5 minutes.
        // i.e. the 8th retry and about will retry every 5 minutes.
        var delayMinutes = Math.min(5, Math.max(item.retry - 3, 1));
        item.waitUntil = DateUtils.fromDate(new Date(Date.now() + delayMinutes * 60 * 1000));

        await this.unshift(item);
      },
      handleException: async (logger: Logger, error: any, entry: Delivery<T>) => {
        var msg = "";
        var state: QueueEntryState = "Error";
        if (error instanceof QueueOperationException) {
          state = error.state;
          msg = error.message;
        }
        else if (error instanceof Error) {
          msg = error.message;
        }
        else if (error instanceof Object) {
          msg = JSON.stringify(error);
        }
        else {
          msg = "Unknown error";
        }
        if (state !== "PendingParent") {
          // This is either an Error or a UnrecoverableError.
          logger.error(`syncUp ${state} encountered: ${msg}.  Payload is ${JSON.stringify(entry)}`);
        }
        else {
          logger.error(`syncUp failed:`, error);
        }

        await entry.failed(state, msg);
      }
    };
  }

  async *iterate(): AsyncGenerator<Delivery<T>> {
    let delivery: Delivery<T> | undefined;
    while ((delivery = await this.pop())) {
      // this loop blocks until the delivery is complete() or failed()
      // because the following iteration will block on pop()
      // due to the queue being locked on the previous delivery.
      yield delivery;
    }
  }

  async clear() {
    await this.lock.run(async () => {
      this.queue = [];
      await this.storage.set('queue', this.queue);
    });
  }
}
